import React from 'react'
import { screenSize } from 'styles/shared'
import Table from 'components/Table'
import SiteMessage from 'pages/shared/SiteMessage'
import Tooltip from 'components/Tooltip'
import styled, { css } from 'styled-components'
import { color } from 'styles/colors'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import _ from 'underscore'
import moment from 'moment/moment'
import { SiteMessageDetail } from 'types/SiteMessageDetail'
import EmptyStatePanel from 'pages/shared/EmptyStatePanel'
import { DEFAULT_NO_RESULTS_MESSAGE } from 'helpers/constants'

type Props = {
  filteredSiteMessages: Array<SiteMessageDetail>
  unreadSiteMessageIds: Array<number>
  onClickCallback: (label: string) => void
  onClickAction: (id: number, label: string) => void
  tabSelection: ('Current' | 'Archived')
}

const UpdateDetail = ({ filteredSiteMessages, unreadSiteMessageIds, onClickCallback, onClickAction, tabSelection = 'Current' }: Props) => {
  const sortSiteMessages = (siteMessagesToSort) => {
    return _.chain(siteMessagesToSort)
      .sortBy((siteMessage) => moment(siteMessage.publishedAt).toDate().getTime())
      .reverse()
      .value()
  }

  const onActionButtonClickCallback = () => {
    onClickCallback('Clicked action button')
  }

  const onImageClickedCallback = () => {
    onClickCallback('Clicked image')
  }

  const onTextClickedCallback = () => {
    onClickCallback('Clicked text link')
  }

  return (
    <>
      {filteredSiteMessages.length === 0 && (
        <EmptyStatePanel
          headingText="No Updates"
          canShowNoResultsBanner
          noResultsHeadingText={DEFAULT_NO_RESULTS_MESSAGE.noResultsHeadingText}
        />
      )}

      {filteredSiteMessages.length > 0 &&
        <>
          <br />
          <Table.Header>
            <S.MessageHeaderCell title="Message" />
            <S.DateHeaderCell title="Date" tabSelection={tabSelection} />
          </Table.Header>
          {sortSiteMessages(filteredSiteMessages).map(filteredSiteMessages => (
            <S.MessageRow key={filteredSiteMessages.id}>
              <S.MessageCell>
                <S.SiteMessage key={filteredSiteMessages.id} isNew={unreadSiteMessageIds?.includes(filteredSiteMessages.id)}>
                  {unreadSiteMessageIds?.includes(filteredSiteMessages.id) && <S.NewLabel>NEW</S.NewLabel>}
                  <SiteMessage.Body
                    showCollapsed={!unreadSiteMessageIds?.includes(filteredSiteMessages.id)}
                    siteMessage={filteredSiteMessages}
                    showDescription
                    onActionButtonClickCallback={onActionButtonClickCallback}
                    onImageClickCallback={onImageClickedCallback}
                    onTextClickCallback={onTextClickedCallback}
                  />
                </S.SiteMessage>
              </S.MessageCell>
              <S.DateCell tabSelection={tabSelection}>
                <>
                  <SiteMessage.PublishedDate publishedAt={filteredSiteMessages.publishedAt} />
                  {tabSelection === 'Current' &&
                    <Tooltip title="Archive Message">
                      <S.ArchiveOutlinedIcon
                        role="img"
                        aria-label="Archive Message"
                        onClick={() => onClickAction(filteredSiteMessages.id, 'archive')}
                      />
                    </Tooltip>
                  }
                  {tabSelection !== 'Current' &&
                    <S.ActionIcons>
                      <Tooltip title="Restore Message">
                        <S.RestoreOutlinedIcon role="img" aria-label="Restore Message" onClick={() => onClickAction(filteredSiteMessages.id, 'restore')} />
                      </Tooltip>
                      <Tooltip title="Delete Message">
                        <S.DeleteOutlinedIcon role="img" aria-label="Delete Message" onClick={() => onClickAction(filteredSiteMessages.id, 'delete')} />
                      </Tooltip>
                    </S.ActionIcons>
                  }
                </>
              </S.DateCell>
            </S.MessageRow>
          ))}
        </>
      }
    </>
  )
}

export default UpdateDetail

const iconStyle = (iconType: string) => {
  return css`
    cursor: pointer;
    color: ${color(iconType)};

    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const S = {
  SiteMessage: styled(SiteMessage).withConfig({
    shouldForwardProp: (prop) => prop !== 'isNew'
  })<{ isNew: boolean }>`
    width: 100%;
   ${({ isNew }) => isNew && css`
   padding-left: 16px;`};
  `,
  NewLabel: styled.div`
    width: 44px;
    height: 21px;
    text-align: center;
    margin-bottom: 7px;
    font-weight: bold;
    border-radius: 5px;
    background: ${color('newLabelBackground')};
    color: ${color('newLabelText')};
  `,
  MessageHeaderCell: styled(Table.HeaderCell)`
    flex: 10;
    min-height: 40px;
    background-color: ${color('customerPinUnpinned')};
    margin-right: 1px;
    border-bottom-left-radius: 0 !important;
    margin-left: 10px;
  `,
  DateHeaderCell: styled(Table.HeaderCell).withConfig({
    shouldForwardProp: (prop) => !['tabSelection'].includes(prop)
  })<{ tabSelection: string }>`
    flex: 1;
    min-height: 40px;
    min-width: ${props => (props.tabSelection === 'Current' ? '130px' : '160px')};
    background-color: ${color('customerPinUnpinned')};
    border-bottom-right-radius: 0 !important;
    margin-right: 10px;
  `,
  MessageRow: styled(Table.Row)`
      padding: 10px 0;
      background-color: ${color('infoPanelHeaderBackground')};
      border-bottom: 1px solid ${color('customerPinUnpinned')};
      margin: 0 10px;
      &:last-child {
          border-bottom: none;
          border-bottom-left-radius: 16px;
          border-bottom-right-radius: 16px;
      }

      @media ${screenSize.small} {
        flex-flow: row wrap;
        padding: 10px 0 10px;
      }
  `,
  MessageCell: styled(Table.Cell)`
      flex: 10 1 0;
      align-items: flex-start;
      min-width: 0;
      flex-direction: column;
      margin-right: 15px;

      @media ${screenSize.small} {
          flex: 1 1 100%;
          margin-right: 0;
          margin-bottom: 10px;
      }
  `,
  DateCell: styled(Table.Cell).withConfig({
    shouldForwardProp: (prop) => !['tabSelection'].includes(prop)
  })<{ tabSelection: string }>`
      display: flex;
      flex: 1;
      align-items: center;
      gap: 10px;
      justify-content: space-between;
      margin-right: 10px;
      min-width: ${props => (props.tabSelection === 'Current' ? '110px' : '140px')};
      @media ${screenSize.small} {
        justify-content: flex-start;
        gap: 20px;
        padding-left: 45px;
      }
  `,
  ActionIcons: styled.div`
    display: flex;  
    gap: 15px;
  `,
  DeleteOutlinedIcon: styled(DeleteOutlinedIcon)`
    ${iconStyle('destructiveIcon')}
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `,
  RestoreOutlinedIcon: styled(RestoreOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `
}
